/**
 * The completion list for a `{{placeholder}}`, drawn at a caller-chosen position.
 *
 * Shared by the prompt body and an `each` variable's item template. Position is a
 * prop because the two anchor differently: a textarea's caret has to be
 * mirror-measured, while a one-line input can sit the menu underneath.
 */
export function PlaceholderMenu({
	matches,
	active,
	hintFor,
	position,
	onAccept,
	id,
}: {
	matches: string[]
	active: number
	/** Optional muted right-hand text — a variable's type, say. */
	hintFor?: (key: string) => string | undefined
	/** Absolute offsets, as CSS accepts them — a pixel number or a percentage. */
	position: { top: number | string; left: number | string }
	onAccept: (key: string) => void
	/** Ties the menu to the field driving it, so `aria-activedescendant` can resolve. */
	id?: string
}) {
	if (matches.length === 0) return null

	return (
		// `role="listbox"` with `role="option"` rows, because `aria-selected` is only
		// meaningful inside one. As a plain `<ul>` of buttons a screen reader ignored the
		// attribute entirely and announced no suggestions at all — the keyboard handling in
		// `PromptBodyEditor` was already correct, so this was purely the missing semantics.
		<ul
			id={id}
			role="listbox"
			aria-label="Variable suggestions"
			style={{ top: position.top, left: position.left }}
			className="absolute z-10 max-h-48 min-w-48 max-w-full overflow-auto rounded-ds-md border border-ds-border bg-ds-surface shadow-ds-md"
		>
			{matches.map((key, index) => (
				// `role="presentation"` on the `<li>`: a listbox's children must be options,
				// and the list item would otherwise sit between them.
				<li key={key} role="presentation">
					{/*
						`onMouseDown` rather than `onClick`: the field's blur fires first on a
						click and would close the menu before the selection landed.
					*/}
					<button
						type="button"
						role="option"
						id={id ? `${id}-option-${index}` : undefined}
						aria-selected={index === active}
						className={`flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-ds-2xs ${
							index === active ? 'bg-ds-surface text-ds-text' : 'text-ds-text-muted'
						}`}
						onMouseDown={(e) => {
							e.preventDefault()
							onAccept(key)
						}}
					>
						<span className="font-mono">{key}</span>
						{hintFor?.(key) === undefined ? null : <span className="text-ds-text-subtle">{hintFor(key)}</span>}
					</button>
				</li>
			))}
		</ul>
	)
}
